import React, { useState } from 'react';
import { auth } from '../../config/firebase';
import { createPortalSession, getAllPrices, resetCustomer } from '../../services/stripeService';

const PricingCard = ({ 
  title, 
  price, 
  period, 
  features, 
  buttonText, 
  secondButtonText, 
  gradientBg = false,
  specialBg = false,
  stripePriceId,
  handleSubscribe,
  isCurrentPlan = false,
  isProcessingPayment = false,
  billingPeriod,
  planType = 'pro',
  developerMode = false,
  isProPlan = false,
  isUserOnProPlan = false,
  openCustomerPortal = null
}) => {
  // Function to handle subscription button click
  const handleClick = async () => {
    console.log('========================================');
    console.log('BUTTON CLICKED FOR PLAN:', title);
    console.log('Price ID:', stripePriceId);
    console.log('Plan Type:', planType);
    console.log('Is processing payment:', isProcessingPayment);
    console.log('Is current plan:', isCurrentPlan);
    console.log('User logged in:', !!auth.currentUser);
    console.log('Button text:', buttonText);
    console.log('Billing period (passed as prop):', billingPeriod);
    console.log('Period suffix (displayed):', period);
    console.log('Price details:', {
      id: stripePriceId,
      title: title,
      price: price,
      period: period,
      planType: planType,
      features: features ? features.join(', ') : 'none'
    });
    console.log('========================================');
    
    try {
      // Check if user is logged in
      if (!auth.currentUser) {
        console.log('User not logged in, redirecting to login page');
        alert('Please log in first to purchase a subscription');
        window.location.href = "/login";
        return;
      }
      
      // If business plan and user is on Pro plan, redirect to customer portal
      if (planType === 'business' && isUserOnProPlan && openCustomerPortal) {
        console.log('User is upgrading from Pro to Business, opening customer portal');
        await openCustomerPortal();
        return;
      }
      
      if (stripePriceId) {
        console.log('Calling handleSubscribe with price ID:', stripePriceId);
        // Use billing period passed as prop
        console.log('Using billing period from props:', billingPeriod);
        console.log('Using plan type from props:', planType);
        await handleSubscribe(stripePriceId, billingPeriod, planType);
        console.log('Returned from handleSubscribe');
      } else {
        // Free plan or other action
        console.log('No price ID, redirecting to dashboard');
        window.location.href = "/dashboard-page";
      }
    } catch (error) {
      console.error('Error in handleClick:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Loading spinner component
  const LoadingSpinner = () => (
    <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite] align-[-0.125em] text-white"></span>
  );

  // Add class for current plan
  const cardBorderClass = isCurrentPlan
    ? 'border-2 border-blue-600'
    : 'border border-zinc-800';
    
  // Card background color
  const cardBgClass = gradientBg 
    ? 'bg-gradient-to-br from-pink-600 via-purple-700 to-yellow-500' 
    : specialBg ? 'bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-500' 
    : 'bg-zinc-900';

  // Determine button text for Business plan
  const displayButtonText = (planType === 'business' && isUserOnProPlan) 
    ? 'Upgrade' 
    : buttonText;

  return (
    <div className={`rounded-3xl p-8 ${cardBorderClass} ${cardBgClass} flex flex-col h-full min-h-[500px] ${isCurrentPlan ? 'ring-2 ring-blue-600' : ''}`}>
      <div className="flex-grow">
        <h3 className="text-2xl font-bold mb-2">{title}</h3>
        <div className="flex items-baseline mb-8">
          <span className="text-6xl font-bold">{price === '0' ? 'Free' : `$${price}`}</span>
          {period && <span className="text-gray-400 ml-2">{period}</span>}
        </div>
        
        <div className="mb-8">
          <h4 className="text-xl mb-4">Includes</h4>
          <ul className="space-y-3">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start text-sm">
                <span className="flex-shrink-0 mr-2 text-xs">✓</span>
                <span className="text-left">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Show ID only in developer mode */}
        {developerMode && stripePriceId && (
          <div className="mb-4 text-xs text-gray-500 break-all">
            <div className="opacity-70">ID: {stripePriceId}</div>
          </div>
        )}
      </div>
      
      <div className="mt-auto">
        {isCurrentPlan ? (
          // If this card represents the current plan
          planType === 'free' ? (
            // Special display for current free plan
            <div className="w-full py-2 px-4 text-center text-blue-400 text-sm font-medium">
              Your Current Plan
            </div>
          ) : (
            // Standard display for current paid plan
            <button disabled className="w-full py-2 px-4 rounded-lg bg-blue-600 text-white text-sm font-medium">
              Current Plan
            </button>
          )
        ) : (
          // If this card is NOT the current plan
          planType === 'free' ? (
            // Don't show anything for the free plan if it's not current
            null
          ) : secondButtonText ? (
             // Logic for plans with two buttons (e.g., Upgrade + Contact)
             <div className="flex space-x-2">
              <button 
                onClick={handleClick}
                disabled={isProcessingPayment}
                className="flex-1 py-2 px-4 rounded-lg bg-white text-black text-sm font-medium hover:bg-gray-200 transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center"
              >
                {isProcessingPayment ? <LoadingSpinner /> : displayButtonText}
              </button>
              <button 
                className="flex-1 py-2 px-4 rounded-lg bg-zinc-800 text-white text-sm font-medium hover:bg-zinc-700 transition-colors"
              >
                {secondButtonText}
              </button>
            </div>
          ) : (
             // Standard action button for non-current, non-free plans
             <button 
               onClick={handleClick}
               disabled={isProcessingPayment}
               className="w-full py-2 px-4 rounded-lg bg-white text-black text-sm font-medium hover:bg-gray-200 transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center"
             >
               {isProcessingPayment ? <LoadingSpinner /> : displayButtonText}
             </button>
          )
        )}
      </div>
    </div>
  );
};

// Component to display test card information
const TestCards = () => {
  const [showCards, setShowCards] = useState(false);
  
  return (
    <div className="mt-12 text-center">
      <button
        onClick={() => setShowCards(!showCards)}
        className="text-sm text-gray-400 underline cursor-pointer"
      >
        {showCards ? 'Hide Test Cards' : 'Show Test Cards for Development'}
      </button>
      
      {showCards && (
        <div className="mt-4 p-4 bg-zinc-800 rounded-lg text-left mx-auto max-w-lg">
          <h3 className="text-lg font-bold mb-2">Stripe Test Cards</h3>
          <p className="text-sm mb-4">Use these cards for testing payments in development:</p>
          
          <div className="space-y-2 text-sm">
            <div>
              <p className="font-medium">Success payment card:</p>
              <code className="block bg-zinc-900 p-2 rounded">4242 4242 4242 4242</code>
            </div>
            
            <div>
              <p className="font-medium">Authentication failure card:</p>
              <code className="block bg-zinc-900 p-2 rounded">4000 0000 0000 0002</code>
            </div>
            
            <div>
              <p className="font-medium">Card requiring additional authentication:</p>
              <code className="block bg-zinc-900 p-2 rounded">4000 0025 0000 3155</code>
            </div>
          </div>
          
          <p className="mt-4 text-xs text-gray-400">
            For all cards use any future expiry date, any 3 digits for CVC, and any valid postal code.
          </p>
        </div>
      )}
    </div>
  );
};

const PricingView = ({ 
  billingPeriod, 
  toggleBillingPeriod, 
  plans, 
  loading, 
  handleSubscribe,
  activeSubscription,
  isProcessingPayment,
  checkAllPrices,
  updatePriceId,
  developerMode,
  useStripePlans,
  togglePlanSource
}) => {
  // Check if user is logged in
  const isLoggedIn = !!auth.currentUser;
  
  // Portal loading state
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  
  // Use developerMode value for displaying dev tools
  const showDevTools = developerMode;
  
  // Check if a given product ID matches the user's CURRENT ACTIVE subscription
  const checkCurrentPlan = (planTypeToCheck) => {
    const hasActiveSub = activeSubscription && (activeSubscription.status === 'active' || activeSubscription.status === 'trialing');
  
    if (planTypeToCheck === 'free') {
      // Free plan is current only if there is NO active subscription
      return !hasActiveSub;
    } else if (hasActiveSub && activeSubscription.product && plans[planTypeToCheck]) {
      // For paid plans, check if the active subscription's product ID matches the plan's product ID
      const planProductId = plans[planTypeToCheck].stripeProductId;
      return planProductId && activeSubscription.product.id === planProductId;
    }
    
    // Otherwise (no active sub, or plan data missing), the plan is not current
    return false;
  };
  
  // Check if user is on the Pro plan
  const isUserOnProPlan = () => {
    if (!activeSubscription || !activeSubscription.product) return false;
    
    const isActive = activeSubscription.status === 'active' || activeSubscription.status === 'trialing';
    const isPro = activeSubscription.product.id === plans.pro.stripeProductId;
    
    return isActive && isPro;
  };
  
  // Open Stripe customer portal
  const handleOpenCustomerPortal = async () => {
    try {
      setIsPortalLoading(true);
      const { url } = await createPortalSession(window.location.origin + '/pricing');
      window.location.href = url;
    } catch (error) {
      console.error('Error opening customer portal:', error);
      
      // Special handling for "No such customer" error
      if (error.message && error.message.includes("No such customer")) {
        if (window.confirm('There seems to be an issue with your customer account. Would you like to reset your data?')) {
          try {
            await resetCustomer();
            alert('Customer data has been reset successfully. Please refresh the page and try again.');
            window.location.reload();
          } catch (resetError) {
            console.error('Error resetting customer:', resetError);
            alert('An error occurred while resetting customer data');
          }
        }
      } else {
        alert('An error occurred while trying to open the customer portal');
      }
      
      setIsPortalLoading(false);
    }
  };
  
  // State for price ID update form
  const [showPriceIdForm, setShowPriceIdForm] = useState(false);
  const [newMonthlyPriceId, setNewMonthlyPriceId] = useState('');
  const [newYearlyPriceId, setNewYearlyPriceId] = useState('');
  const [priceIdPlanType, setPriceIdPlanType] = useState('pro');
  
  // Function to update price IDs
  const handleUpdatePriceIds = (e) => {
    e.preventDefault();
    
    if (newMonthlyPriceId) {
      updatePriceId('monthly', newMonthlyPriceId, priceIdPlanType);
      setNewMonthlyPriceId('');
    }
    
    if (newYearlyPriceId) {
      updatePriceId('year', newYearlyPriceId, priceIdPlanType);
      setNewYearlyPriceId('');
    }
    
    setShowPriceIdForm(false);
  };
  
  // Function to copy price ID to update form
  const copyPriceIdToForm = (priceId, planType = 'pro') => {
    if (billingPeriod === 'monthly') {
      setNewMonthlyPriceId(priceId);
    } else {
      setNewYearlyPriceId(priceId);
    }
    setPriceIdPlanType(planType);
    setShowPriceIdForm(true);
    alert(`The price ID ${priceId} has been copied to the update form`);
  };
  
  // Check if user is currently on Pro plan
  const userOnProPlan = isUserOnProPlan();
  
  return (
    <div className="min-h-screen bg-black text-white py-16 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h1 className="text-6xl font-bold mb-4 sm:text-4xl">Pricing</h1>
          <p className="text-xl text-gray-400 mb-8 sm:text-lg">Choose the plan that works for you</p>
          
          <div className="inline-flex rounded-lg overflow-hidden border border-zinc-800">
            <button
              className={`px-6 py-2 text-xs ${billingPeriod === 'monthly' ? 'bg-zinc-800 text-white' : 'bg-transparent text-gray-400'}`}
              onClick={() => {
                console.log('Toggle to monthly billing period');
                toggleBillingPeriod('monthly');
              }}
            >
              MONTHLY
            </button>
            <button
              className={`px-6 py-2 text-xs ${billingPeriod === 'year' ? 'bg-zinc-800 text-white' : 'bg-transparent text-gray-400'}`}
              onClick={() => {
                console.log('Toggle to yearly billing period');
                toggleBillingPeriod('year');
              }}
            >
              YEARLY (SAVE 20%)
            </button>
          </div>
          
          {/* Display current price IDs */}
          {showDevTools && (
            <div className="mt-2 mb-2 text-xs text-gray-500">
              <div className="opacity-70">
                Free plan has no price ID (free plan)
              </div>
              
              <div className="opacity-70">
                Pro {billingPeriod === 'monthly' ? 'monthly' : 'yearly'} price ID: 
                <span 
                  className="mx-1 underline cursor-pointer" 
                  onClick={() => copyPriceIdToForm(plans.pro.stripePriceId, 'pro')}
                  title="Click to copy to form"
                >
                  {plans.pro.stripePriceId || 'Not set'}
                </span>
              </div>
              
              <div className="opacity-70">
                Business {billingPeriod === 'monthly' ? 'monthly' : 'yearly'} price ID: 
                <span 
                  className="mx-1 underline cursor-pointer" 
                  onClick={() => copyPriceIdToForm(plans.business.stripePriceId, 'business')}
                  title="Click to copy to form"
                >
                  {plans.business.stripePriceId || 'Not set'}
                </span>
              </div>
              
              <div className="opacity-70">
                Transfer one-time price ID: 
                <span 
                  className="mx-1 underline cursor-pointer" 
                  onClick={() => copyPriceIdToForm(plans.transfer.stripePriceId, 'transfer')}
                  title="Click to copy to form"
                >
                  {plans.transfer.stripePriceId || 'Not set'}
                </span>
              </div>
            </div>
          )}
          
          {/* Management buttons */}
          {showDevTools && (
            <div className="mt-4 flex gap-2 justify-center flex-wrap">
              <button
                onClick={checkAllPrices}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs"
              >
                Check Stripe Prices
              </button>
              
              <button
                onClick={togglePlanSource}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-xs"
              >
                {useStripePlans ? 'Use Local Plans' : 'Use Stripe Plans'}
              </button>
              
              <button
                onClick={() => setShowPriceIdForm(!showPriceIdForm)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-xs"
              >
                {showPriceIdForm ? 'Cancel' : 'Update Price IDs'}
              </button>
            </div>
          )}
          
          {/* Price ID update form */}
          {showPriceIdForm && (
            <div className="mt-8 p-4 bg-zinc-800 rounded-lg text-left mx-auto max-w-lg">
              <h3 className="text-lg font-bold mb-4">Update Price IDs</h3>
              <form onSubmit={handleUpdatePriceIds} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Plan Type</label>
                  <select 
                    className="bg-zinc-900 text-white px-3 py-2 rounded-lg w-full"
                    value={priceIdPlanType}
                    onChange={(e) => setPriceIdPlanType(e.target.value)}
                  >
                    <option value="free">Free</option>
                    <option value="pro">Pro</option>
                    <option value="business">Business</option>
                    <option value="transfer">Transfer</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Monthly Price ID</label>
                  <input
                    type="text"
                    className="bg-zinc-900 text-white px-3 py-2 rounded-lg w-full"
                    placeholder="price_xxxxxxxxxx"
                    value={newMonthlyPriceId}
                    onChange={(e) => setNewMonthlyPriceId(e.target.value)}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Yearly Price ID</label>
                  <input
                    type="text"
                    className="bg-zinc-900 text-white px-3 py-2 rounded-lg w-full"
                    placeholder="price_xxxxxxxxxx"
                    value={newYearlyPriceId}
                    onChange={(e) => setNewYearlyPriceId(e.target.value)}
                  />
                </div>
                
                <div className="flex space-x-2">
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    Update
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setShowPriceIdForm(false)}
                    className="px-4 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 h-full">
            <PricingCard 
              {...plans.free} 
              handleSubscribe={handleSubscribe} 
              isCurrentPlan={checkCurrentPlan('free')}
              isProcessingPayment={isProcessingPayment}
              billingPeriod={billingPeriod}
              planType="free"
              developerMode={showDevTools}
              isProPlan={false}
              isUserOnProPlan={userOnProPlan}
              openCustomerPortal={handleOpenCustomerPortal}
            />
            <PricingCard 
              {...plans.pro} 
              handleSubscribe={handleSubscribe} 
              isCurrentPlan={checkCurrentPlan('pro')} 
              isProcessingPayment={isProcessingPayment}
              billingPeriod={billingPeriod}
              planType="pro"
              developerMode={showDevTools}
              isProPlan={false}
              isUserOnProPlan={userOnProPlan}
              openCustomerPortal={handleOpenCustomerPortal}
            />
            <PricingCard 
              {...plans.business} 
              handleSubscribe={handleSubscribe} 
              isCurrentPlan={checkCurrentPlan('business')} 
              isProcessingPayment={isProcessingPayment}
              billingPeriod={billingPeriod}
              planType="business"
              developerMode={showDevTools}
              isProPlan={true}
              isUserOnProPlan={userOnProPlan}
              openCustomerPortal={handleOpenCustomerPortal}
            />
            <PricingCard 
              {...plans.transfer} 
              handleSubscribe={handleSubscribe} 
              isCurrentPlan={checkCurrentPlan('transfer')} 
              isProcessingPayment={isProcessingPayment}
              billingPeriod={billingPeriod}
              planType="transfer"
              developerMode={showDevTools}
              isProPlan={false}
              isUserOnProPlan={userOnProPlan}
              openCustomerPortal={handleOpenCustomerPortal}
              specialBg={true}
            />
          </div>
        )}
        
        {isLoggedIn && activeSubscription && (
          <div className="mt-16 text-center">
            <p className="text-gray-400 mb-4">Manage your existing subscription</p>
            <button 
              onClick={handleOpenCustomerPortal}
              disabled={isPortalLoading}
              className="py-2 px-6 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPortalLoading ? (
                <span className="flex items-center">
                  <span className="mr-2 h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                  Loading...
                </span>
              ) : (
                'Manage Subscription'
              )}
            </button>
          </div>
        )}
        
        {/* Reset button appears only when developerMode is enabled */}
        {showDevTools && isLoggedIn && (
          <div className="mt-8 text-center">
            <div className="text-xs text-red-400 mb-1">
              Encountering "No such customer" error?
            </div>
            <button 
              onClick={async () => {
                if (window.confirm('This action will reset your customer data in Stripe. Continue?')) {
                  try {
                    setIsPortalLoading(true);
                    await resetCustomer();
                    alert('Customer data has been reset successfully. Please refresh the page and try again.');
                    window.location.reload();
                  } catch (error) {
                    console.error('Error resetting customer:', error);
                    alert('An error occurred while resetting customer data');
                  } finally {
                    setIsPortalLoading(false);
                  }
                }
              }}
              className="text-xs py-1 px-3 bg-red-800 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Reset Customer Data
            </button>
            <div className="text-xs text-gray-400 mt-1">
              Only use this option if you are experiencing issues with your customer account
            </div>
          </div>
        )}
        
        {!isLoggedIn && (
          <div className="mt-16 text-center">
            <p className="text-gray-400 mb-4">You need to log in first to purchase a subscription</p>
            <button 
              onClick={() => window.location.href="/login"}
              className="py-2 px-6 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-colors"
            >
              Log In
            </button>
          </div>
        )}
        
        {/* Show test cards only in development environment */}
        {showDevTools && <TestCards />}
      </div>
    </div>
  );
};

export default PricingView; 